import { useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import { useQueryClient } from "@tanstack/react-query";

import {
  useGetAuthStatus,
  useGetSsoProviderMutation,
  useLoginMutation,
  useLogoutMutation
} from "@/api/auth/useAuthAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { ERROR_LOGIN_MAPPING } from "@/components/login/data";
import NotificationContent from "@/components/notification/notificationContent";
import { EXCLUDED_EMAILS_FROM_TRACKING } from "@/constants/user";
import { useSso } from "@/context/SsoProvider";
import useToast, { EToastType } from "@/hooks/ui/useToast";
import { localForagePersister } from "@/lib/react-query";
import { resetStore, RootState } from "@/store";
import { setAuth } from "@/store/slice/authSlice";
import { CustomError } from "@/utils/errors";

import { datadogRum } from "@datadog/browser-rum";

export const useAuth = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const dispatch = useDispatch();
  const { initSso } = useSso();
  const { hasLoggedIn } = useSelector((state: RootState) => state.auth);

  const {
    isLoading: isAuthenticating,
    isSuccess: isAuthenticated,
    isFetched: isAuthChecked
  } = useGetAuthStatus({
    enabled: hasLoggedIn
  });

  const { data: userProfile } = useGetUserProfileAPI({ enabled: hasLoggedIn });

  // Effect to handle DD logging based on the logged in user
  useEffect(() => {
    if (!hasLoggedIn) {
      // Not logged in: clear user but keep session alive so unprotected routes (e.g. login page) are tracked.
      // Stopping the session on logout is handled in the logout onSuccess handler.
      datadogRum.clearUser();
      return;
    }

    // Wait until the profile has loaded before making exclusion decisions
    if (!userProfile?.email_address) return;

    datadogRum.setUserProperty("id", userProfile.email_address);

    if (EXCLUDED_EMAILS_FROM_TRACKING.has(userProfile.email_address)) {
      // Do NOT stopSession() here — it triggers Datadog to auto-restart a new session on next
      // user interaction, creating an infinite stop/restart cycle.
      // Instead, rely on beforeSend (which checks usr.id) to discard all RUM events related to this logged in user.
      // stopSessionReplayRecording() is still needed because replay data bypasses beforeSend.
      datadogRum.stopSessionReplayRecording();
    } else {
      // Normal user: enrich session with identity
      datadogRum.setUserProperty("full_name", userProfile.full_name);
    }
  }, [hasLoggedIn, userProfile]);

  const { mutate: logout, isPending: isLoggingOut } = useLogoutMutation({
    onSuccess: async () => {
      // Clear React Query cache
      await localForagePersister.removeClient();
      queryClient.clear();
      // Stop current session on logout. DD will auto-start a new session on next user interaction (e.g. returning to login page).
      datadogRum.stopSession();
      datadogRum.stopSessionReplayRecording();
      datadogRum.clearUser();
      toast.dismiss();
      resetStore();
    },
    onError: () => {
      showToast({
        type: EToastType.ERROR,
        message: (
          <NotificationContent
            title="Something Went Wrong"
            description="Sorry, we're experiencing an issue processing your request. Please try again."
          />
        ),
        autoClose: 3000
      });
    }
  });

  const { mutate: login, isPending: isLoggingIn } = useLoginMutation({
    onSuccess: (result) => {
      dispatch(
        setAuth({
          accessToken: result.access_token, // TODO: after cookie is set, we can remove this
          refreshToken: result.refresh_token, // TODO: after cookie is set, we can remove this
          hasLoggedIn: true // new flag to indicate user has logged in. to reduce dependency on refresh token and access token
        })
      );
    },
    onError: (error: { message: CustomError }) => {
      const errorCode = (error?.message?.code ??
        "default") as keyof typeof ERROR_LOGIN_MAPPING;

      const title =
        ERROR_LOGIN_MAPPING[errorCode]?.title ??
        ERROR_LOGIN_MAPPING.default.title;

      const description =
        ERROR_LOGIN_MAPPING[errorCode]?.message ??
        ERROR_LOGIN_MAPPING.default.message;

      showToast({
        type: EToastType.ERROR,
        message: (
          <NotificationContent title={title} description={description} />
        ),
        autoClose: 3000
      });
    }
  });

  const { mutateAsync: getSsoProvider, isPending: isGettingSsoProvider } =
    useGetSsoProviderMutation({
      onSuccess: initSso
    });

  const output = useMemo(
    () => ({
      // State
      isGettingSsoProvider,
      isAuthenticating,
      isAuthenticated: hasLoggedIn && isAuthenticated,
      isAuthChecked,
      hasLoggedIn,
      isLoggingOut,
      isLoggingIn,

      // Actions
      login,
      logout,
      getSsoProvider
    }),
    [
      isGettingSsoProvider,
      hasLoggedIn,
      isAuthenticating,
      isAuthenticated,
      isAuthChecked,
      isLoggingOut,
      isLoggingIn,
      login,
      logout,
      getSsoProvider
    ]
  );

  return output;
};
